import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { STAGES, roleLabel } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import WeeklyDigestTile from "@/components/WeeklyDigestTile";
import BirthdayTile from "@/components/BirthdayTile";
import {
  Users, UserPlus, ScrollText, Wrench, CalendarClock, TrendingUp, ArrowRight,
} from "lucide-react";

const KPI = ({ icon: Icon, label, value, testId, tone = "primary" }) => (
  <Card className="card-lift border-border bg-white p-6" data-testid={testId}>
    <div className="mb-3 flex items-center justify-between">
      <div className={`flex h-9 w-9 items-center justify-center rounded-md ${tone === "accent" ? "bg-accent/10 text-accent" : "bg-primary/10 text-primary"}`}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
    <div className="overline mb-1 text-muted-foreground">{label}</div>
    <div className="font-dashboard text-3xl font-bold tracking-tight text-foreground">{value}</div>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kpi, setKpi] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard");
        setKpi(data);
      } catch (e) { /* ignore, dashboard will remain empty */ }
    })();
  }, []);

  if (!kpi) return <div className="p-8 text-center text-muted-foreground">Loading dashboard…</div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline mb-2 text-muted-foreground" data-testid="dashboard-role">{roleLabel(user.role)} view</p>
          <h1 className="font-dashboard text-3xl font-bold tracking-tight">Good to see you, {user.name.split(" ")[0]}.</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Here&apos;s where your practice stands right now.
          </p>
        </div>
        {(user.role === "Admin" || user.role === "Advisor" || user.role === "ServiceRep") && (
          <Button onClick={() => navigate("/app/prospects")} className="gap-2" data-testid="go-prospects-btn">
            <UserPlus className="h-4 w-4" /> Manage prospects
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KPI icon={Users} label="Active clients" value={kpi.total_clients} testId="kpi-total-clients" />
        <KPI icon={UserPlus} label="New prospects" value={kpi.prospects_new} testId="kpi-new-prospects" />
        <KPI icon={ScrollText} label="Plans awaiting approval" value={kpi.plans_pending_approval} tone="accent" testId="kpi-plans-pending" />
        <KPI icon={CalendarClock} label="Reviews due" value={kpi.reviews_due} tone="accent" testId="kpi-reviews-due" />
      </div>

      <Card className="border-border bg-white p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <p className="overline text-muted-foreground">Client journey pipeline</p>
            <h2 className="font-dashboard text-lg font-bold">Where every client stands today</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/app/clients")} className="gap-1" data-testid="view-all-clients">
            View all <ArrowRight className="h-3 w-3" />
          </Button>
        </div>
        <div className="grid gap-4 md:grid-cols-5">
          {STAGES.map((s, i) => (
            <button
              key={s}
              onClick={() => navigate(`/app/clients?stage=${encodeURIComponent(s)}`)}
              data-testid={`pipeline-stage-${i + 1}`}
              className="card-lift group rounded-xl border border-border bg-white p-5 text-left"
            >
              <div className={`stage-chip stage-${i + 1} mb-4 text-[10px]`}>Stage {i + 1}</div>
              <div className="mb-1 font-dashboard text-xs font-semibold text-foreground/80">{s}</div>
              <div className="font-dashboard text-3xl font-bold tracking-tight text-primary">
                {kpi.stage_counts[s] || 0}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">clients</div>
            </button>
          ))}
        </div>
      </Card>

      {(user.role === "Execution" || user.role === "Admin") && (
        <div className="grid gap-4 md:grid-cols-2">
          <KPI icon={Wrench} label="Execution — pending" value={kpi.execution_pending} testId="kpi-exec-pending" />
          <KPI icon={TrendingUp} label="Execution — confirmed" value={kpi.execution_confirmed} testId="kpi-exec-confirmed" />
        </div>
      )}

      {user.role === "Admin" && (
        <div className="grid gap-4 md:grid-cols-2">
          <WeeklyDigestTile />
          <BirthdayTile />
        </div>
      )}
    </div>
  );
}
